import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import AccessDenied from "../../common/AccessDenied/AccessDenied";
import Loader from "../../common/Loader/Loader";
import BetwayAccountFragment from "../../fragments/BetwayAccountFragment";
import PredictionsFragment from "../../fragments/PredictionsFragment";
import PrizesFragment from "../../fragments/PrizesFragment";
import RankingFragment from "../../fragments/RankingFragment";
import RulesFragment from "../../fragments/RulesFragment";
import TermsFragment from "../../fragments/TermsFragment";
import UpcomingMatches from "../../fragments/UpcomingMatches";
import useFansUnitedSDK from "../../hooks/useFansUnitedSDK";
import { authenticateWithToken, getUserRequest } from "../../redux/user";
import { getUserToken, getAuthenticated, getAuthPending, getAuthError } from "../../redux/user/selectors";
import { getFantasyTemplateRequest } from "../../redux/fantasy";
import TabsNavigation from "./components/TabsNavigation";

const MainScreen = () => {
	const [selectedTab, setSelectedTab] = useState(0);
	const [searchParams] = useSearchParams();

	const { i18n } = useTranslation();

	const userToken = useSelector(getUserToken);
	const authenticated = useSelector(getAuthenticated);
	const authPending = useSelector(getAuthPending);
	const authError = useSelector(getAuthError);

	const fansUnitedSDK = useFansUnitedSDK();

	const dispatch = useDispatch();

	// Step 1: Token Extraction from URL
	useEffect(() => {
		const token = searchParams.get("token");
		const country = searchParams.get("country");
		const language = searchParams.get("language") || "en";

		// Step 2: Initial Token Authentication
		if (token) {
			dispatch(authenticateWithToken(token, country, language));
		}

		// Handle language changes
		if (language !== "en") {
			i18n.changeLanguage(language);
			return;
		}

		if (country && ["es", "mx", "pa"].includes(country.toLowerCase())) {
			i18n.changeLanguage("es");
		}
	}, [searchParams, dispatch, i18n]);

	// Step 4: User Profile Fetch
	useEffect(() => {
		if (!userToken || !fansUnitedSDK) {
			return;
		}

		dispatch(getUserRequest(fansUnitedSDK));
	}, [userToken, fansUnitedSDK, searchParams, dispatch]);

	// Fetch fantasy template and groups data when the app loads
	useEffect(() => {
		if (fansUnitedSDK) {
			dispatch(getFantasyTemplateRequest(fansUnitedSDK));
		}
	}, [fansUnitedSDK, dispatch]);

	const goToRules = () => {
		setSelectedTab(4);
	};

	const goToPrizes = () => {
		setSelectedTab(2);
	};

	const screens = [
		<PredictionsFragment goToRules={goToRules} goToPrizes={goToPrizes} />,
		<RankingFragment />,
		<PrizesFragment goToRules={goToRules} />,
		<BetwayAccountFragment />,
		<RulesFragment />,
		<TermsFragment />,
		<UpcomingMatches />,
	];

	// Step 5: UI State Management
	if (authPending) {
		return <Loader />;
	}

	if (authError || !authenticated) {
		return <AccessDenied />;
	}

	return (
		<div>
			<TabsNavigation selectedTab={selectedTab} onTabSelect={setSelectedTab} />
			{screens[selectedTab]}
		</div>
	);
};

export default MainScreen;
